import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import {
  Badge,
  Box,
  GreyGrey30,
  Primary16SemiBold,
  ShadowXs,
  Table,
  TableContainer,
  Tbody,
  Td,
  TextOverflow,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@sequencehq/core-components'
import { toCreditGrantStatusBadgeProps, toMoney } from '@sequencehq/utils'
import { format } from '@sequencehq/utils/dates'
import { FC, useState } from 'react'
import { match } from 'ts-pattern'
import {
  AWAITING_FUNDING_TOOLTIP,
  EMPTY_VALUE_LABEL,
  REFRESH_LABELS
} from 'CreditGrants/creditGrant.constants'
import { useScheduleCreditGrantDetails } from 'CreditGrants/hooks/useScheduleCreditGrantDetails'
import { CreditGrantDetailDrawer } from 'CreditGrants/components/CreditGrantDetailDrawer'

type CreditGrantV2 = Dashboardv99990101Api.CreditGrants.CreditGrantV2

export const ScheduleCreditGrantsSection: FC<{ scheduleId: string }> = ({
  scheduleId
}) => {
  const { grants } = useScheduleCreditGrantDetails({ scheduleId })
  const [selectedGrant, setSelectedGrant] = useState<CreditGrantV2 | null>(null)

  if (!grants.length) {
    return null
  }

  // A schedule can hold several grants; show them oldest-first by start date
  // (grants without one sort last).
  const sortedGrants = [...grants].sort((a, b) =>
    (a.effectiveDate ?? '9999-12-31').localeCompare(
      b.effectiveDate ?? '9999-12-31'
    )
  )

  return (
    <Box data-testid="schedule.creditGrants">
      <Box {...Primary16SemiBold} marginBottom="16px">
        Credit grants
      </Box>
      <TableContainer
        borderRadius="lg"
        overflow="hidden"
        border={`1px solid ${GreyGrey30}`}
        width="100%"
        boxShadow={ShadowXs}
      >
        <Table variant="v2" width="100%">
          <Thead userSelect="none">
            <Tr>
              <Th>Description</Th>
              <Th width={120}>Status</Th>
              <Th>Starts</Th>
              <Th>Expires</Th>
              <Th textAlign="right">Balance</Th>
              <Th textAlign="right">Amount</Th>
            </Tr>
          </Thead>
          <Tbody>
            {sortedGrants.map(grant => (
              <CreditGrantRow
                key={grant.id}
                grant={grant}
                onClick={() => setSelectedGrant(grant)}
              />
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      {selectedGrant && (
        <CreditGrantDetailDrawer
          grant={selectedGrant}
          onClose={() => setSelectedGrant(null)}
        />
      )}
    </Box>
  )
}

const CreditGrantRow: FC<{ grant: CreditGrantV2; onClick: () => void }> = ({
  grant,
  onClick
}) => {
  const { currency } = grant

  const balanceLabel = match(grant.status)
    .with('AWAITING_FUNDING', () => EMPTY_VALUE_LABEL)
    .otherwise(
      () =>
        `${toMoney({
          value: String(grant.remainingBalance),
          currency
        })} / ${toMoney({ value: String(grant.amount), currency })}`
    )

  const statusTooltip = match(grant.status)
    .with('AWAITING_FUNDING', () => AWAITING_FUNDING_TOOLTIP)
    .otherwise(() => undefined)

  return (
    <Tr
      cursor="pointer"
      onClick={onClick}
      data-testid={`creditGrants.row.${grant.id}`}
    >
      <Td>
        <TextOverflow>{grant.name}</TextOverflow>
      </Td>
      <Td>
        {/* Wrapped even without a tooltip, so every badge sits inside the same
            wrapper Tooltip adds and the column stays aligned. */}
        <Tooltip
          label={statusTooltip}
          isDisabled={!statusTooltip}
          data-testid={`creditGrants.row.${grant.id}.status.tooltip`}
        >
          <Badge
            {...toCreditGrantStatusBadgeProps({ status: grant.status })}
            data-testid={`creditGrants.row.${grant.id}.status`}
          />
        </Tooltip>
      </Td>
      <Td>
        {grant.effectiveDate
          ? format(new Date(grant.effectiveDate), 'd MMM yyyy')
          : EMPTY_VALUE_LABEL}
      </Td>
      <Td>
        {grant.expiryDate
          ? format(new Date(grant.expiryDate), 'd MMM yyyy')
          : 'No expiry'}
      </Td>
      <Td
        textAlign="right"
        data-testid={`creditGrants.row.${grant.id}.balance`}
      >
        {balanceLabel}
      </Td>
      <Td textAlign="right">
        {toMoney({ value: String(grant.amount), currency })} /{' '}
        {REFRESH_LABELS[grant.grantRefreshFrequency]}
      </Td>
    </Tr>
  )
}
